import { createBrowserRouter } from 'react-router-dom';

import { AppShell } from '@/components/Layout/AppShell';
import { RequireAuth } from '@/components/RequireAuth';
import { Home } from '@/pages/Home';
import { EventsPage } from '@/pages/Events';
import { Categories } from '@/pages/Categories';
import { Cities } from '@/pages/Cities';
import { EventPage } from '@/pages/Event';
import { CartReview } from '@/pages/CartReview';
import { Checkout } from '@/pages/Checkout';
import { CheckoutSuccess } from '@/pages/CheckoutSuccess';
import { MyTickets } from '@/pages/MyTickets';
import { Navigate } from 'react-router-dom';
import { Login } from '@/pages/Login';
import { PrivacyPolicyPage } from '@/pages/PrivacyPolicy';
import { TermsPage } from '@/pages/Terms';
import { FaqPage } from '@/pages/Faq';
// ROUND7 P1 Store + P5 Auth
import { StorePage } from '@/pages/Store';
import { StoreProductPage } from '@/pages/StoreProduct';
import { StoreCategoryPage } from '@/pages/StoreCategory';
import { ForgotPasswordPage } from '@/pages/ForgotPassword';
import { ResetPasswordPage } from '@/pages/ResetPassword';
// ROUND7 Ola 2 — P2 Memberships + P3 Experiences
import { MembershipsPage } from '@/pages/Memberships';
import { MembershipTierPage } from '@/pages/MembershipTier';
import { ExperiencesPage } from '@/pages/Experiences';
import { ExperienceDetailPage } from '@/pages/ExperienceDetail';
// ROUND17 #9 — Flash sales
import { FlashSalesPage } from '@/pages/FlashSales';
import { FlashSaleDetailPage } from '@/pages/FlashSaleDetail';
import { FlashSaleQueuePage } from '@/pages/FlashSaleQueue';
// ROUND7 Ola 3 — P4 Account hub (nested under RequireAuth)
import { AccountLayout } from '@/pages/account/AccountLayout';
import { AccountDashboard } from '@/pages/account/AccountDashboard';
import { AccountProfile } from '@/pages/account/AccountProfile';
import { AccountPaymentMethods } from '@/pages/account/AccountPaymentMethods';
import { AccountMembership } from '@/pages/account/AccountMembership';
import { AccountNotifications } from '@/pages/account/AccountNotifications';
import { NotFound } from '@/pages/NotFound';
import { SectionPlaceholder } from '@/pages/SectionPlaceholder';
import { RouteError } from '@/components/RouteError';

/**
 * Routing skeleton for TASK-C03.
 *
 * Page bodies marked "TODO TASK-Cxx" are intentional placeholders — the
 * commerce-checkout-agent owns implementation in sprint T1 (Vie 15-Sáb 16).
 *
 * Auth gate: `/checkout`, `/checkout/success`, `/my-tickets` are wrapped
 * in `<RequireAuth>`. Unauthenticated visitors get redirected to
 * `/login?from=<pathname>` and bounced back after sign-in.
 */
export const router = createBrowserRouter([
  {
    element: <AppShell />,
    // ROUND8 UAT-C03 — RouteError (no NotFound): un render throw ya no se
    // enmascara como "404". Rutas inexistentes → catch-all `*` abajo.
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <Home /> },
      // ROUND9 UAT-C07 — '/' es el landing curado; '/eventos' el listado completo.
      { path: '/eventos', element: <EventsPage /> },
      // Sub-secciones de Eventos (nav lateral) — catálogo agrupado real.
      { path: '/eventos/categorias', element: <Categories /> },
      { path: '/eventos/ciudades', element: <Cities /> },
      // Sub-sección de Mis Tickets — Historial (placeholder navegable).
      {
        path: '/mis-tickets/historial',
        element: (
          <SectionPlaceholder
            title="Historial"
            subtitle="El registro de tus entradas usadas, transferidas y canceladas."
          />
        ),
      },
      { path: '/event/:id', element: <EventPage /> },
      { path: '/cart', element: <CartReview /> },
      // ROUND7 P1 Store
      { path: '/store', element: <StorePage /> },
      { path: '/store/:productId', element: <StoreProductPage /> },
      { path: '/store/category/:slug', element: <StoreCategoryPage /> },
      // ROUND7 Ola 2 P2 Memberships
      { path: '/memberships', element: <MembershipsPage /> },
      { path: '/memberships/:tierSlug', element: <MembershipTierPage /> },
      // ROUND7 Ola 2 P3 Experiences
      { path: '/experiences', element: <ExperiencesPage /> },
      { path: '/experiences/:id', element: <ExperienceDetailPage /> },
      // ROUND17 #9 — Flash sales (públicas, no requieren auth para ver)
      { path: '/flash-sales', element: <FlashSalesPage /> },
      { path: '/flash-sales/:id', element: <FlashSaleDetailPage /> },
      // Fila Virtual (C3): la sala de espera tiene URL propia, así sobrevive un
      // refresco — el ticket vive en el backend, no en el componente.
      { path: '/flash-sales/:id/fila', element: <FlashSaleQueuePage /> },
      // ROUND7 P5 Auth
      { path: '/login', element: <Login /> },
      // Destino del enlace del banner de cookies. Publica y sin auth: se lee
      // ANTES de aceptar, que es cuando importa.
      { path: '/privacidad', element: <PrivacyPolicyPage /> },
      { path: '/terminos', element: <TermsPage /> },
      { path: '/faq', element: <FaqPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      {
        element: <RequireAuth />,
        children: [
          { path: '/checkout', element: <Checkout /> },
          { path: '/checkout/success', element: <CheckoutSuccess /> },
          // ROUND17 #3: "Mis Boletas" → "Mis Compras". Nueva ruta canónica
          // `/my-purchases` + alias `/my-tickets` que redirige (preserva
          // links externos / web/app que apunten al viejo path).
          { path: '/my-purchases', element: <MyTickets /> },
          { path: '/my-tickets', element: <Navigate to="/my-purchases" replace /> },
          // ROUND7 Ola 3 P4 Account hub — nested routes under shared layout
          {
            path: '/account',
            element: <AccountLayout />,
            children: [
              { index: true, element: <AccountDashboard /> },
              { path: 'profile', element: <AccountProfile /> },
              { path: 'payment-methods', element: <AccountPaymentMethods /> },
              { path: 'membership', element: <AccountMembership /> },
              { path: 'notifications', element: <AccountNotifications /> },
            ],
          },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
